import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Skeleton } from './ui/skeleton';
import { VideoCard } from './VideoCard';
import { Button } from './ui/button';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useCallback } from 'react';

const PAGE_SIZE = 20;

export function VideoFeed() {
  const { nostr } = useNostr();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['video-feed'],
    queryFn: async ({ pageParam, signal }) => {
      const events = await nostr.query(
        pageParam
          ? [{ kinds: [21], limit: PAGE_SIZE, until: pageParam }]
          : [{ kinds: [21], limit: PAGE_SIZE }],
        { signal }
      );

      // Sort descending by created_at
      const sorted = events.sort((a, b) => b.created_at - a.created_at);
      
      // Return the next cursor (timestamp of the last event)
      const nextCursor = sorted.length > 0 ? sorted[sorted.length - 1].created_at : undefined;
      
      return {
        events: sorted,
        nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as number | undefined,
  });

  // Poll for new videos every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Infinite scroll observer
  const observer = useRef<IntersectionObserver | null>(null);
  const lastVideoElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );
      
      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // Flatten all pages into a single array
  const videos = data?.pages.flatMap((page) => page.events) ?? [];

  if (isLoading && !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="aspect-video w-full rounded-lg" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load videos. Please check your relay connections.</p>
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-sm mx-auto space-y-4">
          <div className="text-6xl mb-4">🎬</div>
          <h2 className="text-2xl font-semibold">No videos yet</h2>
          <p className="text-muted-foreground">
            Be the first to bookmark a video! Click the "Add Bookmark" button to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {videos.map((event) => (
          <VideoCard key={event.id} event={event} />
        ))}
      </div>

      {/* Loading indicator at the bottom */}
      <div ref={lastVideoElementRef} className="flex justify-center py-4">
        {isFetchingNextPage && (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
            <span className="text-sm text-muted-foreground">Loading more videos...</span>
          </div>
        )}
        {!hasNextPage && videos.length > 0 && (
          <p className="text-sm text-muted-foreground">No more videos to load</p>
        )}
      </div>

      {/* Manual load more button as fallback */}
      {hasNextPage && !isFetchingNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            className="border-border hover:bg-accent"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            Load More Videos
          </Button>
        </div>
      )}
    </div>
  );
}